import { createStore, createEffect, createEvent, combine } from 'effector';
import { ApiUtils } from '@shared/utils';
import { RequestStrategyEnum, UsersApi, type WithSignal, type UsersRequest, type UserResponse } from '@shared/api';

const getAccountUsersEffect = createEffect(({ signal, ...request }: WithSignal<UsersRequest>) => UsersApi.getUsers(request, signal));
const getAllUsersEffect = createEffect(({ signal, ...request }: WithSignal<UsersRequest>) => UsersApi.getUsers(request, signal));
const clearEvent = createEvent();

const $dataStore = createStore<UserResponse[]>([])
  .on(getAccountUsersEffect.done, (_, data) => data.result.data ?? [])
  .reset(clearEvent);

const $allUsersStore = createStore<UserResponse[]>([])
  .on(getAllUsersEffect.done, (_, data) => data.result.data ?? [])
  .reset(clearEvent);

const $accountUsersLoadingStore = createStore<boolean>(false)
  .on(getAccountUsersEffect.pending, (_, data) => data)
  .on(getAccountUsersEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getAccountUsersEffect.done, () => false)
  .reset(clearEvent);

const $allUsersLoadingStore = createStore<boolean>(false)
  .on(getAllUsersEffect.pending, (_, data) => data)
  .on(getAllUsersEffect.fail, (_, data) => ApiUtils.abortApiLoadingFailCaseTrigger(data.error))
  .on(getAllUsersEffect.done, () => false)
  .reset(clearEvent);

const $accountUsersErrorStore = createStore<string | null>(null)
  .on(getAccountUsersEffect, () => null)
  // .on(getAccountUsersEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getAccountUsersEffect.fail, (_, data) =>
    ApiUtils.abortApiErrorFailCaseTrigger(data.error, 'Something went wrong while fetching Managers. Please try again.'),
  )
  .on(getAccountUsersEffect.done, () => null)
  .reset(clearEvent);

const $allUsersErrorStore = createStore<string | null>(null)
  .on(getAllUsersEffect, () => null)
  .on(getAllUsersEffect.fail, (_, data) =>
    ApiUtils.abortApiErrorFailCaseTrigger(data.error, 'Something went wrong while fetching Users. Please try again.'),
  )
  .on(getAllUsersEffect.done, () => null)
  .reset(clearEvent);

const $loadingStore = combine($accountUsersLoadingStore, $allUsersLoadingStore, (accountUsers, allUsers) => accountUsers || allUsers);
const $errorStore = combine($accountUsersErrorStore, $allUsersErrorStore, (accountUsers, allUsers) => accountUsers || allUsers);

const getAccountUsersApi = ApiUtils.wrapperApi(getAccountUsersEffect, RequestStrategyEnum.Abort);
const getAllUsersApi = ApiUtils.wrapperApi(getAllUsersEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => {
  getAccountUsersApi.cancel();
  getAllUsersApi.cancel();
});

export const $usersAccountStore = {
  events: {
    getAccountUsers: getAccountUsersApi.trigger,
    getAllUsers: getAllUsersApi.trigger,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    allUsers: $allUsersStore,
    error: $errorStore,
  }),
};
